import { Box, debounce, Loader, Stack, StackProps, styled } from "@tm/components"
import { forwardRef, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useHistory } from "react-router"
import { useLocalization } from "@tm/localization"
import { useOffersModules } from "@tm/context-distribution"
import { channel } from "@tm/models"
import { uniqueId } from "@tm/utils"
import { offersCarouselName, offersCarouselSlots } from "@tm/themes"
import { CarouselPagination } from "./CarouselPagination"
import { OfferItem } from "./OfferItem"
import { OfferTiles } from "./OfferTiles"
import { AdvertisementCategories } from "./AdvertisementCategories"
import { useAdvertisementParts } from "../../useAdvertisementParts"
import { useOffersStore } from "../../store"
import { openPartsList } from "../../helpers"

const GENERATED_WORKTASK_ID = uniqueId()
const ITEM_TILE_MIN_WIDTH = 260
const ITEM_CLASSIC_MIN_WIDTH = 250
const ITEM_SPACING = 8

export type OffersCarouselProps = StackProps & {
    hideAdvertisementCategories?: boolean
}
const CarouselComponent = forwardRef<HTMLDivElement, OffersCarouselProps>((props, ref) => {
    const { hideAdvertisementCategories, ...restProps } = props
    const { translateText } = useLocalization()
    const { selectedCategory, currentCarouselPageIndex } = useOffersStore()
    const boxRef = useRef(null)
    const { hasOfferCarouselModuleTileView } = useOffersModules()
    const itemMinWidth = useMemo(
        () => (hasOfferCarouselModuleTileView ? ITEM_TILE_MIN_WIDTH : ITEM_CLASSIC_MIN_WIDTH),
        [hasOfferCarouselModuleTileView]
    )

    const [boxWidth, setBoxWidth] = useState(0)

    const itemAmount = useMemo(() => {
        const totalItemSpace = itemMinWidth + ITEM_SPACING
        const availableItems = Math.floor((boxWidth + ITEM_SPACING) / totalItemSpace)
        return Math.max(1, availableItems)
    }, [boxWidth, itemMinWidth])

    const history = useHistory()
    const {
        advertisementItems,
        parts,
        isLoading: advertisementItemsLoading,
        advertisementItemsLoaded,
        totalPages,
    } = useAdvertisementParts(selectedCategory?.id ?? undefined, currentCarouselPageIndex, itemAmount)

    const observer = useMemo(() => {
        return new ResizeObserver(
            debounce(([entry]) => {
                setBoxWidth(entry.contentRect.width)
            }, 200)
        )
    }, [])

    useLayoutEffect(() => {
        if (boxRef?.current) {
            observer.observe(boxRef.current)

            return () => observer.disconnect()
        }
    }, [observer, boxRef, selectedCategory])

    const requestPartList = useCallback(
        (showAddToBasketMessage?: boolean) => {
            if (selectedCategory) {
                if (showAddToBasketMessage) {
                    channel("APP").publish("TOAST_MESSAGE/SHOW", { message: translateText(1728), skin: "success" })
                }
                openPartsList(history, GENERATED_WORKTASK_ID, selectedCategory)
            }
        },
        [history, selectedCategory, translateText]
    )

    return (
        <Stack ref={ref} gap={1} flex={1} {...restProps}>
            {!hideAdvertisementCategories && <AdvertisementCategories />}
            <Box ref={boxRef} flex={1} height="100%">
                {advertisementItemsLoading && !hasOfferCarouselModuleTileView && <Loader />}
                {advertisementItemsLoaded && !advertisementItems.length && translateText(1010)}
                {!!selectedCategory &&
                    (hasOfferCarouselModuleTileView ? (
                        // show OfferTiles even when parts is empty to show the Skeleton instead of the Loader
                        <OfferTiles
                            parts={parts}
                            itemMinWidth={itemMinWidth}
                            itemSpacing={ITEM_SPACING}
                            workTaskId={GENERATED_WORKTASK_ID}
                            requestPartList={requestPartList}
                            advertisementsLoading={advertisementItemsLoading}
                            itemAmount={itemAmount}
                        />
                    ) : (
                        !!parts.length && (
                            <Stack direction="row" gap={1} flexWrap="wrap" flex={1} pt={0.5} pb={1} height="100%">
                                {parts.map((part) => (
                                    <OfferItem
                                        key={part.id}
                                        workTaskId={GENERATED_WORKTASK_ID}
                                        part={part}
                                        advertisementCategoryId={selectedCategory.id}
                                        onRequestPartList={requestPartList}
                                    />
                                ))}
                            </Stack>
                        )
                    ))}
            </Box>
            {advertisementItemsLoaded && advertisementItems.length && (
                <CarouselPagination totalPages={totalPages} disabled={advertisementItemsLoading} />
            )}
        </Stack>
    )
})

export const OffersCarousel = styled(CarouselComponent, {
    name: offersCarouselName,
    slot: offersCarouselSlots.root,
    overridesResolver: (_, styles) => styles.root,
})({
    flex: 1,
})
